import React, { forwardRef } from 'react'
import Link from 'next/link'
import classNames from 'classnames'
import { useTranslation } from '@/localization/useTranslation'
import { useProfileCompleteness } from '@/hooks/users/useProfileCompleteness'
import { PROFILE_PERSONAL_INFO_PATH } from '@/constants/profileCompleteness'
import { Logo } from './Logo'

import classes from '../styles.module.scss'
import { toPathname } from '../utils'

interface MenuItem {
  route: string
  text: string
}

interface MobileMenuProps {
  isActive: boolean
  items: MenuItem[]
  pathname: string
  logoSrc: string
  isLoggedIn: boolean
  onLogout: () => void
  onClose: () => void
}

export const MobileMenu = forwardRef<HTMLUListElement, MobileMenuProps>(
  ({ isActive, items, pathname, logoSrc, isLoggedIn, onLogout, onClose }, ref) => {
    const { t } = useTranslation()
    const { shouldShowIndicator } = useProfileCompleteness()
    return (
      <>
        <div
          className={classNames({
            [classes.backdrop]: true,
            [classes.backdrop__active]: isActive,
          })}
          onClick={onClose}
        />
        <ul
          ref={ref}
          data-testid="component-mobile-list"
          className={classNames({
            [classes.menu]: true,
            [classes.menu__active]: isActive,
          })}
        >
          <div className={classes.header}>
            <Logo src={logoSrc} testId="component-mobile-menu-logo" clickable={false} />
          </div>
          {items.map((item) => (
            <Link
              key={item.route}
              href={item.route}
              target={item.route.startsWith('http') ? '_blank' : '_self'}
              data-testid="component-mobile-item"
              className={classNames({
                [classes.item]: true,
                [classes.item__active]: pathname === toPathname(item.route)
              })}
            >
              {item.text}
            </Link>
          ))}
          {isLoggedIn ? (
            <>
              {shouldShowIndicator &&
                <Link
                  href={PROFILE_PERSONAL_INFO_PATH}
                  className={classNames({
                    [classes.item]: true,
                    [classes.item__incomplete]: true
                  })}
                  onClick={onClose}
                >
                  <li>
                    <span className={classes.item__dot} />
                    {t('Complete your profile')}
                  </li>
                </Link>
              }
              <Link
                href='/profile'
                className={classNames({
                  [classes.item]: true,
                  [classes.item__active]: pathname === '/profile'
                })}
              >
                <li>{t('Profile')}</li>
              </Link>
              <div onClick={onLogout} className={classes.item}>
                <li>{t('Logout')}</li>
              </div>
            </>
          ) : (
            <Link
              href='/login'
              className={classNames({
                [classes.item]: true,
                [classes.item__active]: pathname === '/login'
              })}
            >
              <li>{t('Log in')}</li>
            </Link>
          )}
        </ul>
      </>
    )
  }
)

MobileMenu.displayName = 'MobileMenu'
